import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

/** Mirrors the four states the Pulse rail shows for a data connection. */
export type AdConfigConnectionState = 'connected' | 'stale' | 'expired' | 'missing';

export type AdConfigPlatform = 'google' | 'meta';

/** How much of a campaign's configuration the export can read today. */
export type AdConfigCoverage = 'full' | 'partial' | 'none';

export interface AdConfigConnection {
  platform: AdConfigPlatform;
  label: string;
  state: AdConfigConnectionState;
  account: string | null;
  lastSyncAt: string | null;
  campaignCount: number;
  detail: string;
  /** Why the last sync failed, or null when it succeeded. */
  lastSyncError: string | null;
}

export interface AdConfigCampaignRow {
  platform: AdConfigPlatform;
  channel: string;
  name: string;
  externalId: string;
  status: string;
  dailyBudget: number | null;
  /** When an export last read this campaign's settings from the platform. */
  settingsReadAt: string | null;
  /** Removed directly on the platform (e.g. in Google Ads) — read-only here. */
  removedOnPlatform: boolean;
}

export interface AdConfigOverview {
  practice: { id: string; name: string };
  location: { id: string; name: string; city: string | null };
  connections: AdConfigConnection[];
  campaigns: AdConfigCampaignRow[];
}

export interface AdConfigExport {
  filename: string;
  markdown: string;
  generatedAt: string;
  fingerprint: string;
  connections: AdConfigConnection[];
  campaigns: {
    platform: AdConfigPlatform;
    name: string;
    externalId: string;
    coverage: AdConfigCoverage;
    error: string | null;
  }[];
}

export interface AdConfigChange {
  kind: 'added' | 'removed' | 'changed';
  label: string;
  before?: string;
  after?: string;
}

export interface AdConfigSectionDiff {
  section: string;
  changes: AdConfigChange[];
}

export interface AdConfigCampaignDiff {
  key: string;
  heading: string;
  state: 'changed' | 'added' | 'removed';
  sections: AdConfigSectionDiff[];
}

export interface AdConfigDiffResult {
  diff: { campaigns: AdConfigCampaignDiff[]; unchangedCount: number };
  file: { practiceId: string; locationId: string; formatVersion: number };
}

/** A confirmed import saved on the PracticeNova side for review. */
export interface AdConfigDraftSummary {
  id: string;
  /** pending_review | pushed | discarded */
  status: string;
  sourceFilename: string | null;
  fingerprint: string | null;
  fileExportedAt: string | null;
  changedCount: number;
  createdAt: string;
  reviewedAt: string | null;
}

export interface ApplyAdConfigResult {
  draft: AdConfigDraftSummary;
  /** The diff as saved — recomputed live at apply time. */
  diff: { campaigns: AdConfigCampaignDiff[]; unchangedCount: number };
}

export type KeywordOperationType =
  | 'add_keyword'
  | 'remove_keyword'
  | 'pause_keyword'
  | 'enable_keyword'
  | 'add_negative'
  | 'remove_negative';

export interface KeywordOperation {
  id: string;
  type: KeywordOperationType;
  scope: 'ad_group' | 'campaign';
  platform: 'google';
  campaign: { externalId: string; name: string };
  adGroup?: { externalId: string; name: string };
  keyword: string;
  matchType?: string;
  before?: string;
  after?: string;
  preview: string;
}

export interface DraftOperationsResult {
  draftId: string;
  status: string;
  operations: KeywordOperation[];
  summary: {
    total: number;
    byType: Record<KeywordOperationType, number>;
    campaigns: number;
  };
}

export interface PlanIssue {
  level: 'error' | 'warning';
  field: string;
  message: string;
}

export interface CreatePlanGoogleData {
  campaignName: string;
  finalUrl: string;
  bidStrategy: string;
  dailyBudget: number;
  headlines: string[];
  descriptions: string[];
  callouts: string[];
  keywords: { keyword: string; matchType: string }[];
  negativeKeywords: string[];
  zipCodes: string[];
  languages: string[];
}

export interface CreatePlanMetaData {
  campaignName: string;
  objective: string;
  adSetName: string;
  dailyBudget: number;
  placements: string[];
  targeting: { ageMin: number; ageMax: number; genders: string[] };
  ads: {
    name: string;
    headline: string;
    description: string;
    primaryText: string[];
    cta: string;
  }[];
}

export type ChangeMechanism = 'live_edit' | 'replace' | 'recreate';

export interface CampaignFieldChange {
  field: string;
  label: string;
  mechanism: ChangeMechanism;
  money?: boolean;
  status?: boolean;
  summary: string;
  before?: string | null;
  after?: string | null;
}

export interface CampaignDiff {
  channel: 'google_search' | 'meta';
  changes: CampaignFieldChange[];
  hasChanges: boolean;
  hasMoneyImpact: boolean;
  hasRecreate: boolean;
}

interface CreateDraftViewBase {
  sourceCampaign: string;
  adGroup: string;
  fileStatus: string;
  /** Landing URL captured for the brief (Meta drafts). */
  landingUrl?: string;
  issues: PlanIssue[];
  errorCount: number;
  warningCount: number;
  /** A campaign of this name already exists at the location (re-import). */
  existing: boolean;
  /** The existing campaign is already deployed to a platform (live). */
  existingLive?: boolean;
  /** Field-by-field diff of the proposal vs the live campaign, when existingLive. */
  diff?: CampaignDiff;
}

/** Discriminated by `channel`: Google Search vs Meta. */
export type CreateDraftView = CreateDraftViewBase &
  (
    | { channel: 'google_search'; data: CreatePlanGoogleData }
    | { channel: 'meta'; data: CreatePlanMetaData }
  );

export interface CreatePlan {
  ok: boolean;
  formatVersion: number | null;
  drafts: CreateDraftView[];
  skipped: { name: string; reason: string }[];
  fileIssues: PlanIssue[];
  summary: {
    sourceCampaigns: number;
    drafts: number;
    ready: number;
    errors: number;
    warnings: number;
    existing: number;
    readyNew: number;
    existingLive: number;
  };
}

export type ExistingStrategy = 'skip' | 'update';

export interface StageLiveUpdatesResult {
  staged: number;
  deploymentIds: string[];
}

export interface ReplaceFromFileResult {
  /** Campaigns whose PracticeNova copy now carries the file's version. */
  replaced: {
    deploymentId: string;
    marketingCampaignId: string | null;
    name: string;
    changes: number;
    /** Parts of the file that were not taken, and why. */
    notes: string[];
  }[];
  /** Campaigns the file describes exactly as PracticeNova already has them. */
  unchanged: string[];
  /** Campaigns that could not be replaced, with the reason. */
  skipped: { name: string; reason: string }[];
}

export interface CreateDraftsResult {
  created: number;
  updated: number;
  skippedExisting: number;
  skippedLive: number;
  skippedErrors: number;
  marketingCampaignIds: string[];
}

/** What "Re-sync from Google" did: PracticeNova's copy of each live Google Search campaign now
 *  holds Google's settings. Nothing is written to Google. */
export interface GoogleResyncResult {
  refreshed: {
    deploymentId: string;
    name: string;
    changes: { label: string; summary: string }[];
    /** A staged "Update on Google" was moved onto Google's settings, keeping only its own changes. */
    stagedUpdateRebased: boolean;
  }[];
  unchanged: string[];
  skipped: { name: string; reason: string }[];
  syncedAt: string;
  metricsSyncQueued: boolean;
  agent: { recheckStarted: boolean; note: string };
}

export const adConfigApi = {
  /**
   * Diff an uploaded file against what is live now. Writes nothing — this is
   * the read half of import, gating any later apply behind a visible change set.
   */
  diff: async (
    practiceId: string,
    locationId: string,
    markdown: string,
  ): Promise<AdConfigDiffResult> => {
    const { data } = await apiClient.post<ApiResponse<AdConfigDiffResult>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/diff`,
      { markdown },
      { timeout: 120_000 },
    );
    return data.data;
  },

  /**
   * Queue a platform sync. This does not change what an export contains — that
   * always reads live — but it refreshes the campaign inventory the tab shows,
   * which is what makes a newly launched campaign appear.
   */
  sync: async (
    practiceId: string,
    locationId: string,
    platform: AdConfigPlatform,
  ): Promise<void> => {
    await apiClient.post(`/agency/practices/${practiceId}/locations/${locationId}/ad-config/sync`, {
      platform,
    });
  },

  /** Make PracticeNova's copy of this location's live Google campaigns match Google. */
  resyncGoogle: async (practiceId: string, locationId: string): Promise<GoogleResyncResult> => {
    const { data } = await apiClient.post<{ success: boolean; data: GoogleResyncResult }>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/resync-google`,
    );
    return data.data;
  },

  /**
   * Parse an authored file into a plan of new campaigns to create for this
   * location. Writes nothing — pure parse + validation, so the panel can show
   * what would be created (and any errors) before anything is written.
   */
  createPlan: async (
    practiceId: string,
    locationId: string,
    markdown: string,
  ): Promise<CreatePlan> => {
    const { data } = await apiClient.post<ApiResponse<CreatePlan>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/create-plan`,
      { markdown },
    );
    return data.data;
  },

  /**
   * Create PracticeNova campaign drafts from an authored file. Writes campaign
   * records (no Google/Meta write) — the ready campaigns from the plan become
   * drafts in the Campaigns view.
   */
  createDrafts: async (
    practiceId: string,
    locationId: string,
    markdown: string,
    existingStrategy: ExistingStrategy = 'skip',
  ): Promise<CreateDraftsResult> => {
    const { data } = await apiClient.post<ApiResponse<CreateDraftsResult>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/create-drafts`,
      { markdown, existingStrategy },
    );
    return data.data;
  },

  /**
   * Replace PracticeNova's copy of each campaign an exported file describes
   * (matched by Google campaign ID). The new version shows on the campaign's
   * Setup tab, where "Update on Google" pushes it. Writes nothing to Google.
   */
  replaceFromFile: async (
    practiceId: string,
    locationId: string,
    markdown: string,
    filename?: string,
    externalIds?: string[],
  ): Promise<ReplaceFromFileResult> => {
    const { data } = await apiClient.post<ApiResponse<ReplaceFromFileResult>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/replace`,
      { markdown, filename, externalIds },
    );
    return data.data;
  },

  /**
   * Stage the file's changes as pending revisions on the matching live
   * campaigns, for review on their Setup tab. Writes nothing to Google/Meta.
   */
  stageLiveUpdates: async (
    practiceId: string,
    locationId: string,
    markdown: string,
  ): Promise<StageLiveUpdatesResult> => {
    const { data } = await apiClient.post<ApiResponse<StageLiveUpdatesResult>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/stage-live-updates`,
      { markdown },
    );
    return data.data;
  },

  /**
   * Apply a confirmed file: save it as a PracticeNova-side draft for review.
   * Writes nothing to Google or Meta — that is a separate, later push step. Like
   * diff, it re-reads live, so it is slow and gets the long timeout.
   */
  apply: async (
    practiceId: string,
    locationId: string,
    markdown: string,
    filename?: string,
  ): Promise<ApplyAdConfigResult> => {
    const { data } = await apiClient.post<ApiResponse<ApplyAdConfigResult>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/apply`,
      { markdown, filename },
      { timeout: 120_000 },
    );
    return data.data;
  },

  listDrafts: async (practiceId: string, locationId: string): Promise<AdConfigDraftSummary[]> => {
    const { data } = await apiClient.get<ApiResponse<AdConfigDraftSummary[]>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/drafts`,
    );
    return data.data;
  },

  discardDraft: async (
    practiceId: string,
    locationId: string,
    draftId: string,
  ): Promise<AdConfigDraftSummary> => {
    const { data } = await apiClient.post<ApiResponse<AdConfigDraftSummary>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/drafts/${draftId}/discard`,
      {},
    );
    return data.data;
  },

  /**
   * Dry run: the exact Google keyword operations a draft would push, resolved
   * against a fresh live read. Writes nothing. Slow — it reads live — so it
   * carries the long timeout like diff and export.
   */
  draftOperations: async (
    practiceId: string,
    locationId: string,
    draftId: string,
  ): Promise<DraftOperationsResult> => {
    const { data } = await apiClient.get<ApiResponse<DraftOperationsResult>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/drafts/${draftId}/operations`,
      { timeout: 120_000 },
    );
    return data.data;
  },

  getOverview: async (practiceId: string, locationId: string): Promise<AdConfigOverview> => {
    const { data } = await apiClient.get<ApiResponse<AdConfigOverview>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config`,
    );
    return data.data;
  },

  /**
   * Re-reads every campaign from the platform, so it is slow by design. The
   * Markdown comes back as JSON rather than as a file response because the
   * dashboard authenticates with a Bearer header, which a plain download link
   * cannot send — the browser saves the file from this payload instead.
   */
  exportFile: async (practiceId: string, locationId: string): Promise<AdConfigExport> => {
    const { data } = await apiClient.get<ApiResponse<AdConfigExport>>(
      `/agency/practices/${practiceId}/locations/${locationId}/ad-config/export`,
      { timeout: 120_000 },
    );
    return data.data;
  },
};
